import React from 'react'
import { useSelector } from 'react-redux'
import { Box, Flex, PseudoBox, Text } from '@chakra-ui/core'
import { CloseButton } from 'components/ui'
import { isRtlSelector } from 'redux/selectors/general'
import { notifTokens } from 'services/notifications/designTokens'
import { getServiceVisual, SERVICE_VISUALS } from 'services/notifications/serviceVisuals'
import useLocaleText from 'components/useLocaleText'
import LocaleText from 'components/LocaleText'

interface IProps {
  tab: 'unread' | 'all'
  onTabChange: (tab: 'unread' | 'all') => void
  onClose: () => void
  unreadCount: number
  allCount: number
  serviceFilter: string | null
  onServiceFilterChange: (service: string | null) => void
  serviceCounts: Record<string, number>
}

const NavRow: React.FC<{ isActive: boolean; onClick: () => void; children: React.ReactNode; count: number }> = ({
  isActive,
  onClick,
  children,
  count,
}) => (
  <PseudoBox
    as="button"
    onClick={onClick}
    display="flex"
    w="100%"
    alignItems="center"
    justifyContent="space-between"
    h="32px"
    px="10px"
    borderRadius="8px"
    fontSize="13px"
    bg={isActive ? notifTokens.rowActive : 'transparent'}
    color={isActive ? notifTokens.ink : '#475569'}
    fontWeight={isActive ? 600 : 400}
    _hover={{ bg: isActive ? notifTokens.rowActive : notifTokens.rowHover }}
  >
    <Text as="span">{children}</Text>
    <Text as="span" fontSize="11.5px" fontWeight={500} color={isActive ? notifTokens.ink : notifTokens.faint}>
      {count}
    </Text>
  </PseudoBox>
)

// Column 1 of the desktop modal (spec §2) — 212px, background rail-bg. Every known service renders
// a row, even at 0 notifications, so the full service list is always visible; a service with no
// notifications is shown greyed-out and disabled (not clickable).
export const NotificationRail: React.FC<IProps> = ({
  tab,
  onTabChange,
  onClose,
  unreadCount,
  allCount,
  serviceFilter,
  onServiceFilterChange,
  serviceCounts,
}) => {
  const allServices = Object.keys(SERVICE_VISUALS)
  const isRtl = useSelector(isRtlSelector)

  const t_notifications = useLocaleText('mobile_menu_notifications')
  const t_unread = useLocaleText('t_unread_msgs_btn')
  const t_all = useLocaleText('assigned_by_all_filter')
  const t_services = useLocaleText('services')

  return (
    <Flex
      w="212px"
      flexShrink={0}
      flexDirection="column"
      bg={notifTokens.railBg}
      borderRight={`1px solid ${notifTokens.hairline}`}
      h="100%"
    >
      <Box p="20px 16px 14px" position="relative">
        <Text fontSize="15px" fontWeight={600} color={notifTokens.ink}>
          {t_notifications}
        </Text>
        <CloseButton
          position="absolute"
          top="14px"
          left={isRtl ? '12px' : 'auto'}
          right={isRtl ? 'auto' : '12px'}
          minW="26px"
          h="26px"
          p="4px"
          onClick={onClose}
        />
      </Box>

      <Flex flexDirection="column" px="10px" style={{ gap: '1px' }}>
        <NavRow isActive={tab === 'unread'} onClick={() => onTabChange('unread')} count={unreadCount}>
          {t_unread}
        </NavRow>
        <NavRow isActive={tab === 'all'} onClick={() => onTabChange('all')} count={allCount}>
          {t_all}
        </NavRow>
      </Flex>

      {allServices.length > 0 && (
        <>
          <Text
            fontSize="10.5px"
            fontWeight={600}
            color={notifTokens.faint}
            letterSpacing="0.11em"
            textTransform="uppercase"
            p="18px 16px 7px"
          >
            {t_services}
          </Text>
          <Flex flexDirection="column" px="10px" style={{ gap: '1px' }}>
            {allServices.map((service) => {
              const visual = getServiceVisual(service)
              const count = serviceCounts[service] || 0
              const isDisabled = count === 0
              const isActive = !isDisabled && serviceFilter === service
              return (
                <PseudoBox
                  as="button"
                  key={service}
                  onClick={() => {
                    if (isDisabled) return
                    onServiceFilterChange(isActive ? null : service)
                  }}
                  display="flex"
                  w="100%"
                  alignItems="center"
                  justifyContent="space-between"
                  h="32px"
                  px="10px"
                  borderRadius="8px"
                  fontSize="13px"
                  cursor={isDisabled ? 'default' : 'pointer'}
                  bg={isActive ? notifTokens.rowActive : 'transparent'}
                  color={isDisabled ? notifTokens.faint : isActive ? notifTokens.ink : '#475569'}
                  opacity={isDisabled ? 0.5 : 1}
                  fontWeight={isActive ? 600 : 400}
                  _hover={isDisabled ? undefined : { bg: isActive ? notifTokens.rowActive : notifTokens.rowHover }}
                >
                  <Flex alignItems="center" style={{ columnGap: '8px' }}>
                    <Box
                      w="7px"
                      h="7px"
                      borderRadius="50%"
                      bg={isDisabled ? notifTokens.faint : visual.text}
                      flexShrink={0}
                    />
                    <Text as="span">
                      <LocaleText text={visual.label} />
                    </Text>
                  </Flex>
                  <Text
                    as="span"
                    fontSize="11.5px"
                    fontWeight={500}
                    color={isActive ? notifTokens.ink : notifTokens.faint}
                  >
                    {count}
                  </Text>
                </PseudoBox>
              )
            })}
          </Flex>
        </>
      )}

      <Box flex="1" />
    </Flex>
  )
}
